// /series splash — featured directory + TTS/generator promo + migration Alert (D4/D5/C3).
// Loader-only ⇒ pure SSG: fetched once at build, baked into the static HTML. No clientLoader/
// HydrateFallback (those bake a spinner + re-fetch live on hydrate, defeating SSG/SEO).
// Node-prerender-safe: fetch + MUI + local helpers only — no store/Amplify/@shared imports
// (this module's whole graph is eagerly imported during Node prerender, wave-1's gotcha).
// `Mui` (theme+CssBaseline) is pure createTheme()/component code, no browser-global module
// scope — safe to reuse here for brand consistency instead of hand-rolling a second theme.
import { Link as RouterLink } from 'react-router';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import Mui from '../../src/ui/Setup/Mui';
import { MigrationNotice } from './series-migration';
import { fetchFeaturedShows, type FeaturedShow } from './series-data';
import { metaDescription, ORIGIN } from './series-ld';
import type { Route } from './+types/series';

const TITLE = 'Gnothi Series — journals turned into podcasts';
const DESCRIPTION = metaDescription(
  'Browse podcasts Gnothi users generated from their own journals, or turn yours into one.',
);
const SERIES_URL = `${ORIGIN}/series`;

export async function loader(): Promise<{ featured: FeaturedShow[] }> {
  // Fail-soft is deliberate (atom #5) but must not be silent: this runs at PRERENDER, so a transient artifact fetch failure bakes an empty splash into the deploy and the build still goes green. The build log is the only place anyone could ever see it.
  try { return { featured: await fetchFeaturedShows(12) }; }
  catch (e) { console.warn('[series] featured fetch failed — rendering an empty directory', e); return { featured: [] }; }
}

export const meta: Route.MetaFunction = () => [
  { title: TITLE },
  { name: 'description', content: DESCRIPTION },
  { property: 'og:title', content: TITLE },
  { property: 'og:description', content: DESCRIPTION },
  { property: 'og:url', content: SERIES_URL },
  { tagName: 'link', rel: 'canonical', href: SERIES_URL },
];

function ShowCard({ row }: { row: FeaturedShow }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
      <CardActionArea component={RouterLink} to={`/series/${row.slug}`} sx={{ height: '100%', p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Box component="img" src={row.cover_url} alt={row.title}
            sx={{ width: 64, height: 64, borderRadius: 1, objectFit: 'cover', flexShrink: 0, bgcolor: 'action.hover' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600 }} noWrap>{row.title}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>{row.owner_name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {row.episode_count} episode{row.episode_count === 1 ? '' : 's'}
            </Typography>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

// Empty featured (fresh deploy, artifact not yet published) renders nothing — never breaks the
// page (goal atom #5's fail-soft requirement).
function FeaturedShows({ rows }: { rows: FeaturedShow[] }) {
  if (!rows.length) return null;
  return (
    <Box component="section" sx={{ mb: 6 }}>
      <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
        Featured shows
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        Real journals, turned into podcasts by Gnothi users.
      </Typography>
      <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
        {rows.map((row) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={row.id}>
            <ShowCard row={row} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// The `/voice` vanity redirect anchor-scrolls to `#tts` (D6, frozen literal — do not rename).
function TtsPromo() {
  return (
    <Box component="section" id="tts" sx={{ scrollMarginTop: '80px', mb: 6 }}>
      <Card elevation={0} sx={{ p: { xs: 3, md: 4 }, textAlign: 'center', borderRadius: 4, bgcolor: 'background.paper' }}>
        <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
          <RecordVoiceOverIcon color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
            Turn your journal into a podcast
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 560 }}>
            Enable podcasting on any Gnothi story and it narrates your entries in a natural voice,
            publishes a real RSS feed, and lists here if you make it public.
          </Typography>
          <Button component={RouterLink} to="/" variant="contained" size="large" sx={{ px: 4, mt: 1 }}>
            Get started free
          </Button>
        </Stack>
      </Card>
    </Box>
  );
}

export default function SeriesIndex({ loaderData }: Route.ComponentProps) {
  return (
    <Mui>
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <MigrationNotice />
        <Stack spacing={1} sx={{ alignItems: 'center', textAlign: 'center', mb: 5 }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>Gnothi Series</Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 640, fontWeight: 400 }}>
            Podcasts made from journals — listen to what Gnothi users have to say, or make your own.
          </Typography>
        </Stack>
        <FeaturedShows rows={loaderData.featured} />
        <TtsPromo />
      </Container>
    </Mui>
  );
}
